/**
 * AdminSupportChat — Customer Service Command Center "Active Chats" tab.
 *
 * Rep tools (Feb 2026):
 *  - Online / Busy / Offline availability toggle (only reps with a
 *    representative@nobleempirecs.com email can go Online)
 *  - Live list of support sessions (auto-refresh 8s)
 *  - Take Over — claims the chat from Noble, records take_over_at
 *  - Reply templates sidebar (5 canned responses, editable before send)
 *  - Session timer — turns red past 15 minutes
 *  - End Chat — pushes the resolution + 1-5 star prompt to the customer
 *  - Browser notification on new assignment
 *  - Rep leaderboard scorecards (avg stars, resolved %, total chats)
 */
import { useEffect, useRef, useState, useMemo } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { SupportDeliveryStatus } from './SupportDeliveryStatus';
import { SupportReplyTemplates } from './SupportReplyTemplates';
import { SupportConversation } from './SupportConversation';
import { Badge } from './ui/badge';
import {
  MessageCircle, RefreshCw, AlertCircle, Send, X, Handshake, StopCircle,
  Clock, Star, Users, ChevronRight, FileText,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API.replace(/^http/, 'ws');
const REFRESH_MS = 8000;
const TIMER_WARN_MINUTES = 15;

const STATUS_BADGE = {
  active: 'bg-zinc-700 text-zinc-200',
  escalated: 'bg-amber-500 text-black',
  closed: 'bg-zinc-800 text-zinc-500',
};

const AVAIL_BADGE = {
  online:  { label: 'ONLINE',  className: 'bg-emerald-600 text-white' },
  busy:    { label: 'BUSY',    className: 'bg-amber-500 text-black' },
  offline: { label: 'OFFLINE', className: 'bg-zinc-700 text-zinc-300' },
};

function fmtDuration(seconds) {
  if (seconds == null) return '--:--';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function AdminSupportChat({ initialSessionId, autoClaim = false }) {
  const [sessions, setSessions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [transcript, setTranscript] = useState(null);
  // Iter-97 · CS Agent handoff context — visitor type, captured details,
  // filtered response templates, and reroute departments. Loaded together
  // with the transcript so the entire session view lives in one panel.
  const [handoffCtx, setHandoffCtx] = useState(null);
  const [rerouting, setRerouting] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);      // availability doc
  const [templates, setTemplates] = useState([]);
  const [scorecards, setScorecards] = useState([]);
  const [roster, setRoster] = useState([]);
  const [nowTick, setNowTick] = useState(Date.now());
  const [notifPermission, setNotifPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );
  const transcriptRef = useRef(null);
  const timerRef = useRef(null);
  const tickRef = useRef(null);
  const prevUnreadRef = useRef({});
  const audioRef = useRef(null);
  const claimedLinkRef = useRef(null);
  const [claimError, setClaimError] = useState('');
  const selectedRef = useRef(null);
  const draftsRef = useRef({});
  const [claiming, setClaiming] = useState(false);
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  const [ending, setEnding] = useState(false);
  const [listView, setListView] = useState('active');
  const [savingOutcome, setSavingOutcome] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const filterRef = useRef('');
  filterRef.current = new URLSearchParams({ view: listView, q: listView === 'previous' ? historySearch.trim() : '' }).toString();
  useEffect(() => {
    setSessions([]);
    const timer = setTimeout(() => loadSessions(), 250);
    return () => clearTimeout(timer);
  }, [listView, historySearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectSession = (sid) => {
    if (selectedRef.current === sid) return;
    selectedRef.current = sid;
    setReviewConfirmed(false);
    setSelectedId(sid); setTranscript(null); setHandoffCtx(null);
    setReply(draftsRef.current[sid] || ''); setClaimError('');
  };
  const updateReply = (text) => {
    if (selectedRef.current) draftsRef.current[selectedRef.current] = text;
    setReply(text);
  };

  const eligible = status?.eligible ?? false;
  const isOnline = status?.status === 'online';

  // ---------------------------------------------------------------------
  // Loaders
  // ---------------------------------------------------------------------
  const loadStatus = async () => {
    try {
      const res = await fetch(`${API}/api/support-chat/agent/status`, { credentials: 'include' });
      if (res.ok) setStatus(await res.json());
    } catch { /* noop */ }
  };

  const loadTemplates = async () => {
    try {
      const res = await fetch(`${API}/api/support-chat/agent/templates`, { credentials: 'include' });
      if (res.ok) setTemplates(await res.json());
    } catch { /* noop */ }
  };

  const loadScorecards = async () => {
    try {
      const res = await fetch(`${API}/api/support-chat/admin/scorecards`, { credentials: 'include' });
      if (res.ok) setScorecards(await res.json());
    } catch { /* noop */ }
  };

  const loadRoster = async () => {
    try {
      const res = await fetch(`${API}/api/support-chat/agent/roster`, { credentials: 'include' });
      if (res.ok) setRoster(await res.json());
    } catch { /* noop */ }
  };

  const askNotificationPermission = async () => {
    if (typeof Notification === 'undefined') return;
    try {
      const r = await Notification.requestPermission();
      setNotifPermission(r);
    } catch { /* ignore */ }
  };

  const fireDesktopNotification = (session) => {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') return;
    try {
      const n = new Notification('🔔 New chat assigned', {
        body: `${session.user_role || 'Visitor'}: ${(session.last_text || '').slice(0, 120)}`,
        tag: `necs-chat-${session.session_id}`,
        renotify: true,
      });
      n.onclick = () => { window.focus(); selectSession(session.session_id); n.close(); };
    } catch { /* noop */ }
    try { audioRef.current?.play?.(); } catch { /* noop */ }
  };

  const loadSessions = async () => {
    setLoading(true);
    const filter = filterRef.current;
    try {
      const res = await fetch(`${API}/api/support-chat/admin/sessions?${filter}`, { credentials: 'include', cache: 'no-store' });
      if (!res.ok) throw new Error('Chat list unavailable');
      if (res.ok) {
        const list = await res.json();
        if (filter !== filterRef.current) return;
        list.forEach((s) => {
          const prev = prevUnreadRef.current[s.session_id] ?? 0;
          if (s.status === 'escalated' && s.unread_for_admin > prev && s.last_from === 'user') {
            fireDesktopNotification(s);
          }
          prevUnreadRef.current[s.session_id] = s.unread_for_admin;
        });
        setSessions(list);
      }
    } catch { setClaimError('Chats could not be refreshed. Please retry.'); } finally {
      if (filter === filterRef.current) setLoading(false);
    }
  };

  const loadTranscript = async (sid) => {
    if (!sid) return;
    try {
      const res = await fetch(`${API}/api/support-chat/admin/sessions/${sid}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Conversation could not be loaded. Please refresh.');
      const data = await res.json();
      if (selectedRef.current === sid) setTranscript(data);
    // Iter-97 · Also pull the handoff-context payload — visitor type +
    // captured details + templates filtered by visitor type + reroute
    // departments — so the panel becomes a single-view CS cockpit.
    try {
      const r2 = await fetch(`${API}/api/support-chat/admin/sessions/${sid}/handoff-context`, { credentials: 'include' });
      if (r2.ok) {
        const context = await r2.json();
        if (selectedRef.current === sid) setHandoffCtx(context);
      }
    } catch { /* Template failure does not erase a draft. */ }
    } catch (err) { if (selectedRef.current === sid) setClaimError(err.message || 'Could not load this conversation.'); }
  };

  const rerouteToDepartment = async (department) => {
    if (!selectedId || rerouting) return;
    setRerouting(true);
    try {
      const res = await fetch(`${API}/api/support-chat/admin/sessions/${selectedId}/reroute`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ department, note: '' }),
      });
      if (res.ok) {
        await loadTranscript(selectedId);
        await loadSessions();
      }
    } catch { /* silent */ }
    finally { setRerouting(false); }
  };

  // ---------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------
  useEffect(() => {
    loadStatus();
    loadTemplates();
    loadSessions();
    loadScorecards();
    loadRoster();
    timerRef.current = setInterval(() => {
      loadSessions();
      if (selectedId) loadTranscript(selectedId);
      if (isOnline) loadStatus(); // periodic ping keeps rep alive in the round-robin
      loadRoster();
    }, REFRESH_MS);
    return () => clearInterval(timerRef.current);
     
  }, [selectedId, isOnline]);

  // Session timer tick
  useEffect(() => {
    if (!transcript?.take_over_at || transcript.ended_at || transcript.status === 'closed') return;
    tickRef.current = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(tickRef.current);
  }, [transcript?.take_over_at, transcript?.ended_at, transcript?.status]);

  useEffect(() => {
    if (selectedId) loadTranscript(selectedId);
  }, [selectedId]);

  useEffect(() => {
    if (initialSessionId && /^[A-Za-z0-9_-]{1,128}$/.test(initialSessionId)) selectSession(initialSessionId);
  }, [initialSessionId]);

  useEffect(() => {
    if (!autoClaim || !eligible || selectedId !== initialSessionId || transcript?.session_id !== initialSessionId || claimedLinkRef.current === initialSessionId) return;
    claimedLinkRef.current = initialSessionId;
    // Opening a protected link does not confirm that the transcript was read.
    document.querySelector('[data-testid="admin-support-chat"]')?.scrollIntoView({ block: 'start' });
  }, [autoClaim, eligible, initialSessionId, selectedId, transcript?.session_id, transcript?.agent_joined]);

  useEffect(() => {
    if (transcriptRef.current) transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
  }, [transcript]);

  // ---------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------
  const changeStatus = async (next) => {
    try {
      const res = await fetch(`${API}/api/support-chat/agent/status`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: next }),
      });
      if (res.ok) setStatus({ ...(await res.json()), eligible: true });
    } catch { /* noop */ }
  };

  const takeOver = async () => {
    if (!selectedId || claiming || !reviewConfirmed) return;
    const sid = selectedId;
    setClaiming(true);
    setClaimError('');
    try {
      const res = await fetch(`${API}/api/support-chat/admin/sessions/${selectedId}/take-over`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reviewed: true }),
      });
      if (res.ok) {
        const data = await res.json();
        if (selectedRef.current === sid && !draftsRef.current[sid] && data.welcome_draft) updateReply(data.welcome_draft);
        await loadTranscript(sid);
        await loadSessions();
      } else {
        const t = await res.json().catch(() => ({}));
        setClaimError(t.detail || 'Could not take over this chat.');
      }
    } catch { setClaimError('Could not connect. Please try again.'); }
    finally { setClaiming(false); }
  };

  const sendReply = async (e) => {
    if (e) e.preventDefault();
    if (!selectedId || !reply.trim() || sending) return;
    setSending(true);
    const sid = selectedId;
    const sentDraft = reply;
    setClaimError('');
    try {
      const res = await fetch(`${API}/api/support-chat/admin/sessions/${selectedId}/reply`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: reply.trim() }),
      });
      if (res.ok) {
        if (draftsRef.current[sid] === sentDraft) draftsRef.current[sid] = '';
        if (selectedRef.current === sid) setReply(draftsRef.current[sid] || '');
        await loadTranscript(sid);
        await loadSessions();
      } else {
        const t = await res.json().catch(() => ({}));
        setClaimError(t.detail || 'Could not send reply. Your draft is saved.');
      }
    } catch { setClaimError('Could not send reply. Your draft is saved; please retry.'); } finally {
      setSending(false);
    }
  };

  const insertTemplate = (tpl) => {
    updateReply(tpl.text);
    document.querySelector('[data-testid="admin-chat-reply-input"]')?.focus();
  };

  const endChat = async () => {
    if (!selectedId || ending) return;
    if (!window.confirm('End this chat and prompt the customer for a rating?')) return;
    setEnding(true); setClaimError('');
    try {
      const res = await fetch(`${API}/api/support-chat/admin/sessions/${selectedId}/end-chat`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        await loadTranscript(selectedId);
        await loadSessions();
      } else {
        const t = await res.json().catch(() => ({}));
        setClaimError(t.detail || 'Could not end chat.');
      }
    } catch { setClaimError('Could not end chat. Please retry.'); }
    finally { setEnding(false); }
  };

  // Timer since take-over
  const timerSeconds = useMemo(() => {
    if (!transcript?.take_over_at) return null;
    try {
      const start = new Date(transcript.take_over_at).getTime();
      if (transcript.status === 'closed' && transcript.time_to_resolve_seconds != null) return transcript.time_to_resolve_seconds;
      const end = transcript.ended_at || transcript.closed_at || (transcript.status === 'closed' ? transcript.last_at : null);
      return Math.max(0, Math.floor(((end ? new Date(end).getTime() : nowTick) - start) / 1000));
    } catch { return null; }
  }, [transcript, nowTick]);

  const timerWarn = timerSeconds != null && timerSeconds >= TIMER_WARN_MINUTES * 60;

  const totalEscalated = sessions.filter((s) => s.status === 'escalated' && s.unread_for_admin > 0).length;
  const ownChat = transcript?.assigned_agent_id === status?.user_id;
  const ended = !transcript || transcript.status === 'closed' || transcript.handoff_state === 'awaiting_rating';
  const canReply = eligible && !ended && transcript?.agent_joined && ownChat;
  const canTakeOver = eligible && transcript && !transcript.agent_joined && !ended;

  return (
    <div className="space-y-4 scroll-mt-28" data-testid="admin-support-chat">
      {claimError && <p role="alert" className="text-sm text-red-300" data-testid="admin-chat-claim-error">{claimError}</p>}
      {/* Hidden audio for new-chat ping */}
      <audio ref={audioRef} src="https://cdn.jsdelivr.net/gh/anars/blank-audio@master/250-milliseconds-of-silence.mp3" preload="auto" />

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <MessageCircle className="h-5 w-5 text-[#FFD60A]" />
            LIVE CUSTOMER CHATS
            {totalEscalated > 0 && (
              <Badge className="bg-amber-500 text-black animate-pulse" data-testid="admin-chat-escalated-count">
                {totalEscalated} escalated
              </Badge>
            )}
          </h3>
          <p className="text-xs text-zinc-400">
            External customer chats only — internal Crew &amp; Region chat lives in Operations. Auto-refresh 8s.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {notifPermission === 'default' && (
            <Button variant="outline" size="sm" onClick={askNotificationPermission}
                    className="border-amber-500 text-amber-400" data-testid="admin-chat-enable-notifications">
              🔔 Enable desktop alerts
            </Button>
          )}
          {notifPermission === 'granted' && (
            <Badge className="bg-emerald-700 text-white text-[10px]" data-testid="admin-chat-notif-on">
              🔔 Desktop alerts ON
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={() => { loadSessions(); loadRoster(); loadScorecards(); }} disabled={loading}
                  className="border-zinc-700 text-zinc-300" data-testid="admin-chat-refresh">
            <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Availability + Roster row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card className="bg-zinc-900 border-zinc-700 p-3" data-testid="admin-chat-availability">
          <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Your live-agent status</p>
          {!eligible ? (
            <p className="text-xs text-amber-400">
              Live-agent seat restricted to <span className="font-mono">*representative@nobleempirecs.com</span> accounts.
              You can still view every transcript below.
            </p>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={AVAIL_BADGE[status?.status || 'offline']?.className}>
                {AVAIL_BADGE[status?.status || 'offline']?.label}
              </Badge>
              <span className="text-[11px] text-zinc-400">
                Active chats: <span className="font-bold text-white">{status?.active_chats || 0}</span> · Total: {status?.total_chats || 0}
              </span>
              <div className="flex gap-1 ml-auto">
                <Button size="sm" variant={status?.status === 'online' ? 'default' : 'outline'}
                        onClick={() => changeStatus('online')}
                        className={status?.status === 'online' ? 'bg-emerald-600 text-white h-7 px-3' : 'border-zinc-600 text-zinc-300 h-7 px-3'}
                        data-testid="admin-chat-set-online">Online</Button>
                <Button size="sm" variant={status?.status === 'busy' ? 'default' : 'outline'}
                        onClick={() => changeStatus('busy')}
                        className={status?.status === 'busy' ? 'bg-amber-500 text-black h-7 px-3' : 'border-zinc-600 text-zinc-300 h-7 px-3'}
                        data-testid="admin-chat-set-busy">Busy</Button>
                <Button size="sm" variant={status?.status === 'offline' ? 'default' : 'outline'}
                        onClick={() => changeStatus('offline')}
                        className={status?.status === 'offline' ? 'bg-zinc-700 text-white h-7 px-3' : 'border-zinc-600 text-zinc-300 h-7 px-3'}
                        data-testid="admin-chat-set-offline">Offline</Button>
              </div>
            </div>
          )}
        </Card>

        <Card className="bg-zinc-900 border-zinc-700 p-3" data-testid="admin-chat-roster">
          <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1 flex items-center gap-1">
            <Users className="h-3 w-3" /> Rep Roster
          </p>
          {roster.length === 0 ? (
            <p className="text-xs text-zinc-500">No representatives seeded yet.</p>
          ) : (
            <div className="space-y-1 max-h-24 overflow-y-auto">
              {roster.map((r) => (
                <div key={r.user_id} className="flex items-center justify-between text-[11px]">
                  <span className="text-zinc-300 truncate">{r.name || r.email}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-zinc-500">{r.active_chats || 0} live</span>
                    <Badge className={`${AVAIL_BADGE[r.status || 'offline']?.className} text-[9px] px-1.5 py-0`}>
                      {AVAIL_BADGE[r.status || 'offline']?.label}
                    </Badge>
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="bg-zinc-900 border-zinc-700 p-3" data-testid="admin-chat-scorecards">
          <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1 flex items-center gap-1">
            <Star className="h-3 w-3" /> Rep Leaderboard
          </p>
          {scorecards.length === 0 ? (
            <p className="text-xs text-zinc-500">No ratings yet — the leaderboard populates after the first rated chat.</p>
          ) : (
            <div className="space-y-1 max-h-24 overflow-y-auto">
              {scorecards.slice(0, 5).map((s, idx) => (
                <div key={s.user_id} className="flex items-center justify-between text-[11px]">
                  <span className="text-zinc-300 truncate">#{idx + 1} {s.name || s.user_id}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-[#FFD60A] font-bold">⭐ {s.avg_stars ?? '—'}</span>
                    <span className="text-zinc-500">{s.resolved_count || 0}/{s.total_chats || 0}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="admin-chat-view-tabs">
        {['active', 'previous'].map(view => <Button key={view} variant={listView === view ? 'default' : 'outline'} onClick={() => setListView(view)} aria-pressed={listView === view} data-testid={`admin-chat-view-${view}`}>{view === 'active' ? 'Active conversations' : 'Previous conversations'}</Button>)}
      </div>
      {listView === 'previous' && <div className="flex flex-wrap items-center gap-2">
        <Input type="search" value={historySearch} maxLength={128} aria-label="Search previous support conversations" placeholder="Search topics or messages" onChange={e => setHistorySearch(e.target.value)} className="max-w-md bg-zinc-900 border-zinc-600 text-white" data-testid="admin-chat-history-search" />
        {historySearch && <Button variant="outline" size="icon" title="Clear search" aria-label="Clear search" onClick={() => setHistorySearch('')} data-testid="admin-chat-history-search-clear"><X className="h-4 w-4" /></Button>}
      </div>}
      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Session list */}
        <div className="space-y-2 lg:col-span-1 max-h-[600px] overflow-y-auto" data-testid="admin-chat-session-list">
          {sessions.filter(s => listView === 'previous' ? s.status === 'closed' : s.status !== 'closed').length === 0 && (
            <Card className="p-6 bg-zinc-900 border-zinc-700 text-zinc-500 text-sm text-center" data-testid="admin-chat-empty">
              No support chats yet.
            </Card>
          )}
          {sessions.filter(s => listView === 'previous' ? s.status === 'closed' : s.status !== 'closed').map((s) => {
            const isSel = s.session_id === selectedId;
            return (
              <button
                key={s.session_id}
                onClick={() => selectSession(s.session_id)}
                className={`w-full text-left p-3 rounded-lg border transition ${isSel ? 'bg-amber-900/30 border-amber-500' : 'bg-zinc-900 border-zinc-700 hover:border-zinc-500'}`}
                data-testid={`admin-chat-session-${s.session_id}`}
              >
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Badge className={`${STATUS_BADGE[s.status] || 'bg-zinc-700'} text-[10px] uppercase`}>{s.status}</Badge>
                  {s.escalate && s.unread_for_admin > 0 && (
                    <Badge className="bg-red-600 text-white text-[10px] flex items-center gap-1">
                      <AlertCircle className="h-2.5 w-2.5" /> {s.unread_for_admin} new
                    </Badge>
                  )}
                  {s.agent_joined && (
                    <Badge className="bg-emerald-600 text-white text-[10px]">LIVE</Badge>
                  )}
                </div>
                <p className="text-xs text-zinc-300 break-words" data-testid={`admin-chat-visitor-${s.session_id}`}>{s.visitor_name || s.user_role || 'Visitor'} · {new Date(s.last_at).toLocaleString()}</p>
                {s.topic_label && <p className="text-xs text-[#C9A962] break-words" data-testid={`admin-chat-topic-${s.session_id}`}>{s.topic_label}</p>}
                {s.status === 'closed' && <p className="text-xs text-zinc-300" data-testid={`admin-chat-outcome-${s.session_id}`}>{(s.outcome || 'needs_review').replaceAll('_', ' ')}</p>}
                <p className="text-xs text-zinc-400 truncate mt-1">
                  {s.last_from === 'user' ? '👤' : s.last_from === 'agent' ? '🎧' : '🤖'} {s.last_text || '—'}
                </p>
              </button>
            );
          })}
        </div>

        {/* Transcript + composer */}
        <div className="lg:col-span-2 min-w-0">
          {selectedId && <SupportDeliveryStatus key={selectedId} sessionId={selectedId} />}
          {!transcript ? (
            <Card className="p-10 bg-zinc-900 border-zinc-700 text-center text-zinc-500 text-sm" data-testid="admin-chat-no-selection">
              {selectedId ? 'Loading conversation…' : 'Select a chat from the left to view the conversation.'}
            </Card>
          ) : (
            <Card className="bg-zinc-900 border-zinc-700 flex flex-col overflow-hidden" style={{ height: 620 }}>
              {/* Header row: user + status + timer + actions */}
              <div className="px-4 py-3 border-b border-zinc-700 flex items-center justify-between flex-wrap gap-2">
                <div className="min-w-0 max-w-full text-xs text-zinc-300">
                  <p className="font-bold text-white break-words" data-testid="admin-chat-visitor-name">{transcript.visitor_name || `${transcript.user_role || 'Public'} visitor`}</p>
                  <p className="text-[10px] text-zinc-500 [overflow-wrap:anywhere] max-w-full" data-testid="admin-chat-session-details">
                    Session {transcript.session_id} · {new Date(transcript.started_at).toLocaleString()}
                    {transcript.assigned_agent_name && <> · assigned to <span className="text-emerald-400">{transcript.assigned_agent_name}</span></>}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Session Timer */}
                  {transcript.take_over_at && (
                    <div
                      className={`flex items-center gap-1 text-xs font-mono font-bold px-2 py-1 rounded-md ${timerWarn ? 'bg-red-600 text-white animate-pulse' : 'bg-zinc-800 text-emerald-400'}`}
                      data-testid="admin-chat-timer"
                    >
                      <Clock className="h-3 w-3" /> {fmtDuration(timerSeconds)}
                    </div>
                  )}
                  <Badge className={`${STATUS_BADGE[transcript.status] || 'bg-zinc-700'} text-[10px] uppercase`}>{transcript.status}</Badge>
                  {canTakeOver && (
                    <div className="flex flex-wrap items-center gap-2"><label className="text-xs text-zinc-200 flex items-center gap-2"><input type="checkbox" checked={reviewConfirmed} onChange={e=>setReviewConfirmed(e.target.checked)} data-testid="admin-chat-review-confirmation"/>I reviewed this conversation</label><Button onClick={takeOver} disabled={claiming || !reviewConfirmed} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 px-3" data-testid="admin-chat-take-over">
                      <Handshake className="h-3 w-3 mr-1" /> Connect & send welcome
                    </Button></div>
                  )}
                  {canReply && (
                    <Button onClick={endChat} disabled={ending || sending} size="sm" variant="outline"
                            className="border-amber-500 text-amber-400 h-7 px-3" data-testid="admin-chat-end-chat">
                      <StopCircle className="h-3 w-3 mr-1" /> End Chat
                    </Button>
                  )}
                  {transcript.customer_rating && (
                    <Badge className="bg-[#FFD60A] text-black text-[10px]" data-testid="admin-chat-rating-badge">
                      ⭐ {transcript.customer_rating.stars}/5 · {transcript.customer_rating.resolved ? 'Resolved' : 'Unresolved'}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Transcript */}
              <div ref={transcriptRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="admin-chat-transcript">
                {transcript.previous_conversation && <details data-testid="admin-chat-linked-history">
                  <summary className="text-[#C9A962] cursor-pointer" data-testid="admin-chat-linked-history-toggle">Previous conversation</summary>
                  <SupportConversation messages={transcript.previous_conversation.messages || []} testIdPrefix="admin-linked-history" />
                </details>}
                {transcript.status === 'closed' && <label className="block text-sm text-zinc-300">Saved outcome
                  <select className="block w-full mt-2 bg-zinc-800 p-2 rounded" value={transcript.outcome || 'needs_review'} disabled={savingOutcome} data-testid="admin-chat-saved-outcome" onChange={async e => {
                    const outcome = e.target.value; setSavingOutcome(true);
                    try {
                      const r = await fetch(`${API}/api/support-chat/admin/sessions/${selectedId}/outcome`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ outcome }) });
                      if (!r.ok) throw new Error('Could not save the outcome.');
                      await loadTranscript(selectedId); await loadSessions();
                    } catch (e) { setClaimError(e.message); } finally { setSavingOutcome(false); }
                  }}>
                    <option value="resolved">Resolved</option><option value="needs_review">Needs review</option><option value="follow_up">Follow-up needed</option>
                  </select>
                </label>}
                {(transcript.messages || []).map((m, i) => (
                  <div key={i} className={`flex ${m.from === 'user' ? 'justify-start' : 'justify-end'}`}>
                    <div
                      className="max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere]"
                      data-testid={`admin-chat-message-${i}`}
                      style={
                        m.from === 'user'
                          ? { backgroundColor: '#3D3D3D', color: '#fff', borderBottomLeftRadius: 4 }
                          : m.from === 'agent'
                            ? { backgroundColor: '#10b981', color: '#fff', borderBottomRightRadius: 4 }
                            : m.from === 'system'
                              ? { backgroundColor: '#2a2a2a', color: '#C9A962', fontStyle: 'italic', fontSize: '0.75rem', border: '1px dashed #C9A962' }
                              : { backgroundColor: '#FFD60A', color: '#2D2D2D', borderBottomRightRadius: 4 }
                      }
                    >
                      <p className="text-[9px] uppercase tracking-wider opacity-70 mb-0.5">
                        {m.from === 'user' ? 'Visitor' : m.from === 'agent' ? (m.agent_name || 'Agent') : m.from === 'system' ? 'System' : 'AI · NOBLE'}
                        <span className="ml-2 opacity-60">{m.ts ? new Date(m.ts).toLocaleTimeString() : ''}</span>
                      </p>
                      {m.text}
                    </div>
                  </div>
                ))}
                {(transcript.messages || []).length === 0 && (
                  <p className="text-center text-xs text-zinc-500 py-10">No messages yet in this session.</p>
                )}
              </div>

              {/* Composer */}
              <form onSubmit={sendReply} className="px-4 py-3 border-t border-zinc-700 flex gap-2">
                <textarea
                  value={reply}
                  onChange={(e) => updateReply(e.target.value)}
                  placeholder={canReply ? 'Reply as a Noble Empire specialist…' : ended ? 'This chat has ended.' : 'Take over this chat to reply.'}
                  className="flex-1 min-w-0 bg-zinc-800 border border-zinc-700 text-white placeholder:text-zinc-500 rounded p-2 text-sm resize-none"
                  disabled={sending || !canReply}
                  maxLength={4000}
                  rows={2}
                  data-testid="admin-chat-reply-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); }
                  }}
                />
                <Button
                  type="submit"
                  disabled={!reply.trim() || sending || !canReply}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold self-stretch"
                  data-testid="admin-chat-reply-send"
                >
                  <Send className="h-4 w-4 mr-1" />
                  {sending ? '…' : 'Send'}
                </Button>
              </form>
            </Card>
          )}
        </div>

        {/* Templates sidebar */}
        <div className="lg:col-span-1 min-w-0">
          <Card className="bg-zinc-900 border-zinc-700 p-3 max-h-[620px] overflow-y-auto" data-testid="admin-chat-templates">
            {/* Iter-97 · Handoff-context header — visitor type + captured
                details + department reroute row. Sits above the template
                library so the CS agent sees everything in one panel. */}
            {handoffCtx && (
              <div className="mb-3 pb-3 border-b border-zinc-800" data-testid="admin-chat-handoff-context">
                <p className="text-[10px] uppercase tracking-widest text-[#C9A962] font-bold mb-1">
                  Visitor Type
                </p>
                <p className="text-xs text-white font-bold mb-2" data-testid="admin-chat-visitor-type">
                  {handoffCtx.visitor_type_label || 'Unknown'}
                </p>
                {handoffCtx.captured_details && Object.values(handoffCtx.captured_details).some(Boolean) && (
                  <div className="mb-2" data-testid="admin-chat-captured-details">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1">
                      Captured
                    </p>
                    <ul className="text-[11px] text-zinc-300 space-y-0.5 [overflow-wrap:anywhere]">
                      {handoffCtx.captured_details.visitor_name && <li data-testid="admin-chat-context-name">• Name: <b>{handoffCtx.captured_details.visitor_name}</b></li>}
                      {handoffCtx.captured_details.topic && <li data-testid="admin-chat-context-topic">• Topic: <b>{handoffCtx.captured_details.topic}</b></li>}
                      {handoffCtx.captured_details.property_name && (<li>• Property: <b>{handoffCtx.captured_details.property_name}</b></li>)}
                      {handoffCtx.captured_details.service_line_interest && (<li>• Service line: <b>{handoffCtx.captured_details.service_line_interest}</b></li>)}
                      {handoffCtx.captured_details.issue_type && (<li>• Issue: <b>{handoffCtx.captured_details.issue_type}</b></li>)}
                      {handoffCtx.captured_details.email && (<li>• Email: <b>{handoffCtx.captured_details.email}</b></li>)}
                    </ul>
                  </div>
                )}
                <p className="text-[10px] uppercase tracking-widest text-[#C9A962] font-bold mb-1">
                  Reroute
                </p>
                <div className="flex flex-wrap gap-1" data-testid="admin-chat-reroute-buttons">
                  {(handoffCtx.reroute_departments || ['operations', 'sales', 'noble_ai']).map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => rerouteToDepartment(d)}
                      disabled={rerouting || !canReply}
                      className="text-[10px] px-2 py-1 rounded border border-purple-700/60 bg-purple-900/20 hover:bg-purple-900/40 text-purple-200 uppercase tracking-widest font-bold transition"
                      data-testid={`admin-chat-reroute-${d}`}
                    >
                      {d.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2 flex items-center gap-1">
              <FileText className="h-3 w-3" /> Reply Templates
              {handoffCtx?.visitor_type_label && (
                <span className="text-[9px] text-zinc-500">· {handoffCtx.visitor_type_label}</span>
              )}
            </p>
            <SupportReplyTemplates templates={handoffCtx?.templates || templates} topicKey={handoffCtx?.topic_key}
              sessionId={selectedId} disabled={!canReply || sending || !handoffCtx} onInsert={insertTemplate} />
          </Card>
        </div>
      </div>
    </div>
  );
}
